import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, pending, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // ดึงข้อมูลทีมทั้งหมด
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const teamsRef = collection(db, 'teamRequests');
      const q = query(teamsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTeams(teamsData);
    } catch (error) {
      console.error('Error fetching teams:', error);
      alert('เกิดข้อผิดพลาดในการดึงข้อมูลทีม');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  // เปลี่ยนสถานะทีม
  const updateTeamStatus = async (teamId, newStatus) => {
    try {
      const teamRef = doc(db, 'teamRequests', teamId);
      await updateDoc(teamRef, {
        status: newStatus,
        statusUpdatedAt: new Date().toISOString(),
        statusUpdatedBy: 'admin'
      });

      alert(`เปลี่ยนสถานะทีมเป็น "${getStatusText(newStatus)}" สำเร็จ!`);
      await fetchTeams();
    } catch (error) {
      console.error('Error updating team status:', error);
      alert('เกิดข้อผิดพลาดในการเปลี่ยนสถานะ');
    }
  };

  // กรองทีม
  const filteredTeams = teams.filter(team => {
    const matchesFilter = filter === 'all' || team.status === filter;
    const matchesSearch = 
      team.girlName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.momoName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // สีของสถานะ
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ไอคอนของสถานะ
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'accepted': return '✅';
      case 'rejected': return '❌';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  // ข้อความสถานะ
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รอการอนุมัติ';
      case 'accepted': return 'อนุมัติแล้ว';
      case 'rejected': return 'ปฏิเสธ';
      case 'cancelled': return 'ยกเลิก';
      default: return 'ไม่ระบุ';
    }
  };

  // รูปแบบวันที่
  const formatDate = (date) => {
    if (!date) return 'ไม่ระบุ';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">จัดการทีม</h2>
        <button
          onClick={fetchTeams}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
        >
          🔄 รีเฟรช
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="ค้นหาทีม (ชื่อน้อง, ชื่อโม, ID)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">ทุกสถานะ</option>
          <option value="pending">รอการอนุมัติ</option>
          <option value="accepted">อนุมัติแล้ว</option>
          <option value="rejected">ปฏิเสธ</option>
          <option value="cancelled">ยกเลิก</option>
        </select>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-600">
            {teams.filter(t => t.status === 'pending').length}
          </div>
          <div className="text-sm text-gray-600">รอการอนุมัติ</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {teams.filter(t => t.status === 'accepted').length}
          </div>
          <div className="text-sm text-gray-600">อนุมัติแล้ว</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">
            {teams.filter(t => t.status === 'rejected').length}
          </div>
          <div className="text-sm text-gray-600">ปฏิเสธ</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-600">
            {teams.filter(t => t.status === 'cancelled').length}
          </div>
          <div className="text-sm text-gray-600">ยกเลิก</div>
        </div>
      </div>

      {/* Teams List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ทีม
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  สถานะ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  วันที่สมัคร
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  การดำเนินการ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTeams.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {team.girlName || 'ไม่ระบุชื่อน้อง'}
                      </div>
                      <div className="text-sm text-gray-500">
                        โม: {team.momoName || 'ไม่ระบุชื่อโม'}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {team.id}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(team.status)}`}>
                      <span className="mr-1">{getStatusIcon(team.status)}</span>
                      {getStatusText(team.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(team.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedTeam(team);
                          setShowDetailsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 transition"
                      >
                        ดูรายละเอียด
                      </button>
                      {team.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateTeamStatus(team.id, 'accepted')}
                            className="text-green-600 hover:text-green-900 transition"
                          >
                            อนุมัติ
                          </button>
                          <button
                            onClick={() => updateTeamStatus(team.id, 'rejected')}
                            className="text-red-600 hover:text-red-900 transition"
                          >
                            ปฏิเสธ
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Team Details Modal */}
      {selectedTeam && showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">รายละเอียดทีม</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ID ทีม</label>
                  <p className="text-sm text-gray-900">{selectedTeam.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">สถานะ</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTeam.status)}`}>
                    <span className="mr-1">{getStatusIcon(selectedTeam.status)}</span>
                    {getStatusText(selectedTeam.status)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">น้อง</label>
                  <p className="text-sm text-gray-900">{selectedTeam.girlName || 'ไม่ระบุ'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">โม</label>
                  <p className="text-sm text-gray-900">{selectedTeam.momoName || 'ไม่ระบุ'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">วันที่สมัคร</label>
                <p className="text-sm text-gray-900">{formatDate(selectedTeam.createdAt)}</p>
              </div>

              {selectedTeam.message && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">ข้อความ</label>
                  <p className="text-sm text-gray-900">{selectedTeam.message}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  ปิด
                </button>
                {selectedTeam.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        updateTeamStatus(selectedTeam.id, 'accepted');
                        setShowDetailsModal(false);
                      }}
                      className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                    >
                      อนุมัติทีม
                    </button>
                    <button
                      onClick={() => {
                        updateTeamStatus(selectedTeam.id, 'rejected');
                        setShowDetailsModal(false);
                      }}
                      className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                    >
                      ปฏิเสธทีม
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;









